<template>
    <div class="container">
        <h1 class="container__standard-header">
            <template v-if="accountTypes.length === 1">
                {{ $t(`${TRANSLATION_KEY}.stepAccountTypeHeaderOnlyStock`) }}
            </template>
            <template v-else>
                {{ $t(`${TRANSLATION_KEY}.stepAccountTypeHeader`) }}
            </template>
        </h1>
        <transition name="fade" mode="out-in">
            <div
                v-if="isAccountTypeErrorPopupOpen"
                class="account-type-error-popup-container"
                cy-data="popup"
            >
                <div class="account-type-error-popup">
                    <button
                        type="button"
                        class="account-type-error-popup__close-btn"
                        aria-label="Close popup"
                        @click="handleAccountTypeErrorPopupClose"
                    >
                        <img
                            src="@/assets/close_icon.svg"
                            alt=""
                            aria-hidden="true"
                        />
                    </button>
                    <div class="account-type-error-popup__icon-wrapper">
                        <img src="@/assets/bad.svg" alt="" aria-hidden="true" />
                    </div>
                    <h2 class="account-type-error-popup__header">
                        {{ $t(`${TRANSLATION_KEY}.errorPopupHeader`) }}
                    </h2>
                    <p class="account-type-error-popup__text">
                        {{ accountTypeError }}
                    </p>
                    <button
                        type="button"
                        class="account-type-error-popup__understand-btn"
                        @click="handleAccountTypeErrorPopupClose"
                    >
                        {{
                            $t(
                                `${TRANSLATION_KEY}.${accountTypes.length === 1 ? 'errorPopupButtonOne' : 'errorPopupButtonTwo'}`
                            )
                        }}
                    </button>
                </div>
            </div>
        </transition>

        <form @submit.prevent="null">
            <p class="container__standard-header fs-15">
                <template v-if="accountTypes.length === 1">
                    {{
                        $t(`${TRANSLATION_KEY}.chooseAccountTypeInfoOnlyStock`)
                    }}
                </template>
                <template v-else>
                    {{ $t(`${TRANSLATION_KEY}.chooseAccountTypeInfo`) }}
                </template>
            </p>

            <div class="row">
                <label
                    v-for="account in accountTypes"
                    :key="account.key"
                    :class="[
                        'account-card',
                        hasAccountTypeError ? 'has-error' : '',
                    ]"
                >
                    <input
                        v-model="formData.accountType"
                        type="radio"
                        name="accountType"
                        :value="account.key"
                    />

                    <div class="account-card__content">
                        <h3>{{ $t(account.title) }}</h3>

                        <p
                            v-if="account.description"
                            class="account-card__content__desc"
                        >
                            {{ $t(account.description) }}
                        </p>

                        <ul>
                            <li v-for="item in account.items" :key="item">
                                <img
                                    class="account-card__content__icon"
                                    src="@/assets/arrow_right.svg"
                                    alt=""
                                    aria-hidden="true"
                                />
                                <span
                                    class="account-card__content__item-text"
                                    >{{ $t(item) }}</span
                                >
                            </li>
                        </ul>
                    </div>
                </label>
            </div>
            <SubmitButton
                :action="Action.ACCOUNT_TYPE"
                :button-value="$t(`${TRANSLATION_KEY}.next`)"
                :data="formData"
                class="button button--submit"
                :disabled="!formData.accountType"
                @click="
                    () =>
                        amplitudeSendEvent(
                            AmplitudeAnalyticsEvents.accountType
                                .CHOOSE_ACCOUNT_TYPE_CONTINUE
                        )
                "
            >
            </SubmitButton>
        </form>
    </div>
</template>

<script lang="ts">
import {
    defineComponent,
    ref,
    computed,
    reactive,
    onMounted,
    watch,
} from 'vue';
import SubmitButton from '@/components/basic/buttons/SubmitButton.vue';
import {
    AmplitudeAnalyticsEvents,
    amplitudeSendEvent,
} from '@/utils/AmplitudeAnalyticsEvents';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { Action } from '@/constants/ajax';
import { DELETE_ERROR } from '@/constants/mutation-types';

export default defineComponent({
    name: 'AccountType',
    components: {
        SubmitButton,
    },
    setup() {
        const TRANSLATION_KEY = 'steps.AccountType';

        const store = useStore();
        const { t, locale } = useI18n();

        const allowedAccountTypes = ref<Record<string, string> | null>(null);
        const isAccountTypeErrorPopupOpen = ref(false);

        const getInitialAccountType = () => {
            const accountType =
                store.state.formData.accountType ??
                store.state.response?.dataContent?.accountType ??
                store.state.response?.accountType;

            return accountType !== null && accountType !== undefined
                ? String(accountType)
                : null;
        };

        const formData = reactive({
            accountType: getInitialAccountType() as string | null,
        });

        const accountTypeError = computed(() =>
            store.state.errors.get('accountType')
        );
        const hasAccountTypeError = computed(() => !!accountTypeError.value);

        const handleAccountTypeErrorPopupClose = async () => {
            if (accountTypes.value.length === 1) {
                window.location.replace(
                    `https://www.oanda.com/eu-${locale.value.toLowerCase()}`
                );
                return;
            }

            isAccountTypeErrorPopupOpen.value = false;
        };
        const getAccountDefinitions = () => {
            return {
                'TMS Connect': {
                    title: `${TRANSLATION_KEY}.connectTitle`,
                    description: t(`${TRANSLATION_KEY}.connectDescription`),
                    items: [
                        t(`${TRANSLATION_KEY}.connectItem1`),
                        t(`${TRANSLATION_KEY}.connectItem2`),
                        t(`${TRANSLATION_KEY}.connectItem3`),
                        t(`${TRANSLATION_KEY}.connectItem4`),
                        t(`${TRANSLATION_KEY}.connectItem5`),
                        t(`${TRANSLATION_KEY}.connectItem6`),
                    ],
                },
                'TMS Stocks': {
                    title: t(`${TRANSLATION_KEY}.stockTitle`),
                    description: t(`${TRANSLATION_KEY}.stocksDescription`),
                    items: [
                        t(`${TRANSLATION_KEY}.stockItem1`),
                        t(`${TRANSLATION_KEY}.stockItem2`),
                    ],
                },
            };
        };

        const accountTypes = computed(() => {
            const allowed =
                allowedAccountTypes.value ||
                store.state.formData.allowedAccountTypes ||
                null;

            if (!allowed) return [];

            const definitions = getAccountDefinitions();

            return Object.entries(allowed)
                .sort(([, labelA], [, labelB]) =>
                    String(labelB).localeCompare(String(labelA))
                )
                .map(([key, label]) => ({
                    key,
                    ...(definitions[label as keyof typeof definitions] || {}),
                }));
        });

        /**
         * Fetch allowed account types from backend (AJAX).
         * This is called whenever `allowedAccountTypes` is missing/empty.
         */
        const fetchAllowedAccountTypes = async (): Promise<void> => {
            store.state.isLocked = 'true';

            const ajaxService = store.state.ajaxService;

            try {
                const response = await ajaxService.post(
                    ajaxService.RESOURCES[Action.GET_ALLOWED_ACCOUNT_TYPES],
                    {
                        token: ajaxService.getToken(),
                    }
                );

                if (response.status === 'error') {
                    store.state.globalError = response.errors;
                    return;
                }

                const allowed =
                    response.allowedAccountTypes ??
                    response.data?.allowedAccountTypes ??
                    null;

                if (allowed && Object.keys(allowed).length > 0) {
                    store.state.formData.allowedAccountTypes = allowed;
                    store.state.isLocked = false;

                    allowedAccountTypes.value = allowed;
                }
            } catch (error: any) {
                store.state.globalError = error;
            } finally {
                store.state.isLocked = false;
            }
        };

        watch(
            () => formData.accountType,
            (value) => {
                if (!value) return;

                store.commit(DELETE_ERROR, 'accountType');
            }
        );

        watch(
            () => store.state.isErrorsChanged,
            () => {
                isAccountTypeErrorPopupOpen.value = hasAccountTypeError.value;
            }
        );

        onMounted(() => {
            if (
                !store.state.formData.allowedAccountTypes ||
                Object.keys(store.state.formData.allowedAccountTypes).length ===
                    0
            ) {
                fetchAllowedAccountTypes();
            } else {
                allowedAccountTypes.value =
                    store.state.formData.allowedAccountTypes;
            }

            isAccountTypeErrorPopupOpen.value = hasAccountTypeError.value;
        });

        return {
            TRANSLATION_KEY,
            accountTypes,
            accountTypeError,
            formData,
            hasAccountTypeError,
            isAccountTypeErrorPopupOpen,
            amplitudeSendEvent,
            AmplitudeAnalyticsEvents,
            Action,
            handleAccountTypeErrorPopupClose,
        };
    },
});
</script>
